/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { api } from "../lib/api";
import { speak } from "../lib/i18n";
import { ArrowLeft, HeartPulse, Droplets, Phone, Volume2, Armchair, Candy, Stethoscope, Siren, Ban, Eye, Loader2, WifiOff, Smartphone } from "lucide-react";
import { loadCachedTeam, fetchTeam } from "./WorryMode";
import { FollowupCard } from "../components/FollowupCard";

const ALERTS_KEY = "bc_critical_alerts";

export const GESTES = {
  inquietude: { titre: "Inquiétude signalée", ln: "Mitungisi", color: "#0A1426", Icon: Siren, gestes: [] },
  tension: {
    titre: "Tension très élevée", ln: "Tansio likolo mingi", color: "#D9534F", Icon: HeartPulse,
    gestes: [
      { Icon: Armchair, fr: "Asseoir ou allonger la personne au calme, desserrer les vêtements.", ln: "Fandisa to lalisa ye na kimia, fungola bilamba." },
      { Icon: Ban, fr: "Pas de sel, pas de café, pas d'effort. Rester avec elle.", ln: "Mungwa te, kafe te, mosala te. Tikala na ye." },
      { Icon: HeartPulse, fr: "Reprendre la tension après 15 minutes de repos.", ln: "Meka tansio lisusu sima ya miniti 15." },
      { Icon: Siren, fr: "Si visage tombant, bras faible, parole difficile ou mal de tête violent → URGENCE immédiate.", ln: "Soki elongi ekiti, lobɔkɔ elembi, koloba pasi, motó mpasi makasi → URGENCE sikoyo." },
    ],
  },
  glycemie: {
    titre: "Glycémie anormale", ln: "Glycémie ya mabe", color: "#D4AF5A", Icon: Droplets,
    gestes: [
      { Icon: Candy, fr: "Si glycémie BASSE (< 60) : donner vite du sucre (3 morceaux, jus, miel), puis un petit repas.", ln: "Soki glycémie ezali NSE (< 60) : pesa sukali noki (biteni 3, jus, mafuta ya nzoi), sima bilei moke." },
      { Icon: Droplets, fr: "Si glycémie HAUTE (> 250) : faire boire de l'eau, pas de sucre, repos.", ln: "Soki glycémie ezali LIKOLO (> 250) : mela mai, sukali te, pema." },
      { Icon: Stethoscope, fr: "Vérifier que les médicaments ont été pris comme prescrit.", ln: "Tala soki nkisi emelami ndenge monganga alobaki." },
      { Icon: Siren, fr: "Somnolence, confusion, vomissements ou malaise → appeler le soignant ou les urgences.", ln: "Mpɔngi, mobulu na motó, kosanza to malaise → benga monganga to urgences." },
    ],
  },
};

export default function CaregiverAlertsPage() {
  const navigate = useNavigate();
  const { patient, lang, online } = useApp();
  const [alerts, setAlerts] = useState(() => { try { return JSON.parse(localStorage.getItem(ALERTS_KEY) || "null"); } catch { return null; } });
  const [team, setTeam] = useState(loadCachedTeam);
  const ln = lang === "ln";

  useEffect(() => {
    if (!patient) return;
    Promise.all([api.get(`/patients/${patient.id}/critical-alerts`), fetchTeam(patient.id)])
      .then(([a, t]) => { setAlerts(a.data); localStorage.setItem(ALERTS_KEY, JSON.stringify(a.data)); setTeam(t); return api.post(`/patients/${patient.id}/critical-alerts/seen`); })
      .then(() => window.dispatchEvent(new CustomEvent("bc-alerts-seen")))
      .catch(() => setAlerts((a) => a || []));
  }, [patient, online]);

  if (!patient) return <div className="pt-10 text-center text-[#0A1426]/70">Aucun patient lié.</div>;
  if (!alerts) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div>;

  const types = [...new Set(alerts.slice(0, 5).map((a) => a.type).filter((t) => t !== "inquietude"))];
  const shown = types.length ? types : ["tension", "glycemie"];
  const readAll = (type) => speak(`${ln ? GESTES[type].ln : GESTES[type].titre}. ` + GESTES[type].gestes.map((g) => (ln ? g.ln : g.fr)).join(" "), lang);

  return (
    <div className="bc-in pt-2 pb-6 flex flex-col gap-4" data-testid="caregiver-alerts-page">
      <div className="flex items-center gap-3">
        <button data-testid="alerts-back" onClick={() => navigate("/app")} className="w-11 h-11 grid place-items-center rounded-full bg-white border border-[#E4E0D5] text-[#0A1426]"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="font-[Fraunces] text-2xl font-bold text-[#0A1426] leading-tight">{ln ? "Ba alerte ya urgence" : "Alertes urgentes"}</h1>
          <div className="text-[12px] text-[#0A1426]/60">{team?.patient_nom} · {ln ? "Nini ya kosala" : "Que faire en cas de malaise"}</div>
        </div>
      </div>

      {!online && (
        <div data-testid="alerts-offline-banner" className="rounded-2xl bg-[#D4AF5A] text-[#0A1426] text-[12px] font-bold px-4 py-2 flex items-center gap-2"><WifiOff size={14} /> {ln ? "Hors-ligne — banimero mpe ba alerte ya nsuka na cache" : "Hors-ligne — numéros et dernières alertes en cache"}</div>
      )}
      <button data-testid="worry-btn" onClick={() => navigate("/app/urgence")}
        className="w-full h-16 rounded-3xl bg-[#D9534F] text-white font-[Fraunces] text-xl font-bold flex items-center justify-center gap-3 shadow-lg shadow-[#D9534F]/30 active:scale-[0.98] transition-transform">
        <Siren size={26} className="text-[#D4AF5A]" /> {ln ? "Nazali na mitungisi" : "Je suis inquiet"}
      </button>
      <FollowupCard />

      <section data-testid="emergency-numbers" className="rounded-3xl p-4 bg-[#D9534F] text-white">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-white/85"><Siren size={14} /> {ln ? "Benga" : "Appeler"}</div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {team?.therapeute?.telephone && (
            <a data-testid="call-therapist" href={`tel:${team.therapeute.telephone}`} className="h-14 rounded-2xl bg-white text-[#D9534F] font-bold flex items-center justify-center gap-2 text-[14px]"><Phone size={18} /> {team.therapeute.nom}</a>
          )}
          <a data-testid="call-emergency" href={`tel:${team?.numero_urgence || "112"}`} className="h-14 rounded-2xl bg-[#0A1426] text-white font-bold flex items-center justify-center gap-2 text-[14px]"><Siren size={18} /> {ln ? "Urgences" : "Urgences"} {team?.numero_urgence || "112"}</a>
        </div>
        <p className="text-[11px] text-white/85 mt-2">{team?.numero_urgence ? (ln ? "Nimero ebongisami na Profil." : "Numéro d'urgence défini dans votre Profil.") : (ln ? "112 / 113 : urgences RDC. Bongisa nimero na yo na Profil." : "112 / 113 : urgences RDC. Vous pouvez définir votre propre numéro dans le Profil.")}</p>
      </section>

      <section className="bg-white rounded-3xl border border-[#E4E0D5] p-4" data-testid="alerts-list">
        <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold mb-2">{ln ? "Ba alerte ya nsuka" : "Dernières alertes"} ({alerts.length})</div>
        {alerts.length === 0 && <div data-testid="alerts-empty" className="text-[13px] text-[#0A1426]/60 py-3">{ln ? "Alerte moko te. Nyonso ezali malamu." : "Aucune alerte urgente. Tout va bien."}</div>}
        {alerts.slice(0, 10).map((a) => {
          const g = GESTES[a.type] || GESTES.tension;
          return (
            <div key={a.id} data-testid={`alert-item-${a.type}`} className="flex items-start gap-3 py-2.5 border-b border-[#F5F3EE] last:border-0">
              <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: `${g.color}1f`, color: g.color }}><g.Icon size={20} /></span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold text-[#0A1426]">{ln ? g.ln : g.titre}</div>
                <div className="text-[12px] text-[#0A1426]/80 leading-snug">{a.texte}</div>
                <div className="text-[10px] text-[#0A1426]/50 mt-0.5 flex items-center gap-1">{new Date(a.date).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })} {a.vu_par?.length ? <><Eye size={10} /> {ln ? "emonani" : "vu"}</> : null}</div>
              </div>
            </div>
          );
        })}
      </section>

      {shown.map((type) => {
        const g = GESTES[type];
        return (
          <section key={type} data-testid={`gestes-${type}`} className="bg-white rounded-3xl border border-[#E4E0D5] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><g.Icon size={20} style={{ color: g.color }} /><h2 className="font-[Fraunces] text-lg font-bold text-[#0A1426]">{ln ? g.ln : g.titre} — {ln ? "nini ya kosala" : "que faire"}</h2></div>
              <button data-testid={`gestes-speak-${type}`} onClick={() => readAll(type)} className="w-11 h-11 rounded-full bg-[#0A1426] text-white grid place-items-center"><Volume2 size={18} /></button>
            </div>
            <ol className="flex flex-col gap-2.5">
              {g.gestes.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-12 h-12 rounded-2xl bg-[#F5F3EE] text-[#0A1426] grid place-items-center shrink-0 relative"><s.Icon size={22} /><span className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-[#D4AF5A] text-[#0A1426] text-[10px] font-bold grid place-items-center">{i + 1}</span></span>
                  <p className="text-[14px] text-[#0A1426] leading-snug pt-1">{ln ? s.ln : s.fr}</p>
                </li>
              ))}
            </ol>
          </section>
        );
      })}
      <p className="text-[11px] text-[#0A1426]/60 text-center">{ln ? "Ezali avis ya monganga te." : "Ces gestes ne remplacent pas un avis médical. En cas de doute, appelez."}</p>
      <button data-testid="alerts-install-shortcut" onClick={() => navigate("/installer")} className="w-full rounded-2xl bg-white border border-[#E4E0D5] p-3 flex items-center gap-3 text-left">
        <img src="/icon-urgence.svg" alt="" className="w-10 h-10 rounded-xl shrink-0" />
        <div className="flex-1"><div className="text-[13px] font-bold text-[#0A1426]">{ln ? "Bakisa « Nazali na mitungisi » na écran d'accueil" : "Ajouter « Je suis inquiet » à l'écran d'accueil"}</div><div className="text-[11px] text-[#0A1426]/60">{ln ? "Raccourci ya noki" : "Raccourci en un geste, même sans réseau"}</div></div>
        <Smartphone size={18} className="text-[#0A1426]/50" />
      </button>
    </div>
  );
}
